import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useStoreAuth } from '../../contexts/StoreAuthContext';
import api from '../../services/api';
import StoreHeader from '../../components/Layout/StoreHeader';
import VoiceRecorder from '../../components/VoiceRecorder';
import CameraCapture from '../../components/CameraCapture';
import { Html5QrcodeScanner, Html5QrcodeScanType } from 'html5-qrcode';
import { getPacificToday } from '../../utils/dateUtils';
import './StoreCheckin.css';

// Animal type to emoji mapping
const animalEmojis: Record<string, string> = {
  chickens: '🐔',
  laying_hens: '🐔',
  meat_birds: '🐓',
  ducks: '🦆',
  geese: '🦢',
  turkeys: '🦃',
  goats: '🐐',
  dairy_goats: '🐐',
  meat_goats: '🐐',
  sheep: '🐑',
  pigs: '🐷',
  cattle: '🐄',
  horses: '🐴',
  rabbits: '🐰',
  dogs: '🐕',
  cats: '🐈',
  llamas: '🦙',
  alpacas: '🦙',
  fish: '🐟',
  other: '🐾',
};

interface Animal {
  id: number;
  animal_type: string;
  name: string;
  breed: string;
  count: number;
  weight_lbs: number | null;
  daily_consumption_lbs: number | null;
}

interface Purchase {
  id: number;
  date: string;
  product_name: string;
  quantity: number;
  total_lbs: number | null;
}

interface StaffNote {
  id: number;
  note: string;
  created_by: string;
  created_at: string;
}

interface Member {
  id: number;
  name: string;
  email: string;
  phone: string;
  member_code: string;
  city: string;
  state: string;
  joined_date: string;
  is_approved: boolean;
}

interface FeedStatus {
  animal_id: number;
  animal_type: string;
  animal_name: string;
  count: number;
  daily_consumption_lbs: number | null;
  last_purchase_date: string | null;
  product_name: string | null;
  days_remaining: number | null;
  status: 'ok' | 'warning' | 'critical' | 'unknown';
}

interface CheckinData {
  member: Member;
  last_visit: {
    date: string | null;
    days_ago: number | null;
    is_first_visit: boolean;
  };
  animals: Animal[];
  recent_purchases: Purchase[];
  staff_notes: StaffNote[];
}

interface RecentCheckin {
  id: number;
  member_id: number;
  member_name: string;
  checkin_time: string;
  logged_by: string;
  total: number | null;
  items_count: number;
}

interface ReceiptItem {
  name: string;
  quantity: number;
  price: number;
}

const StoreCheckin: React.FC = () => {
  const { user, isAuthenticated, loading: authLoading } = useStoreAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pageLoading, setPageLoading] = useState(true);

  // View states
  const [view, setView] = useState<'scan' | 'profile' | 'receipt' | 'saved'>('scan');

  // Search states
  const [searchCode, setSearchCode] = useState('');
  const [searchPhone, setSearchPhone] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Member data states
  const [memberData, setMemberData] = useState<CheckinData | null>(null);
  const [feedStatus, setFeedStatus] = useState<FeedStatus[]>([]);
  const [loadingFeedStatus, setLoadingFeedStatus] = useState(false);

  // Recent checkins
  const [recentCheckins, setRecentCheckins] = useState<RecentCheckin[]>([]);

  // Notes
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);

  // Receipt scanning
  const [receiptItems, setReceiptItems] = useState<ReceiptItem[]>([]);
  const [receiptTotal, setReceiptTotal] = useState<number>(0);
  const [scanningReceipt, setScanningReceipt] = useState(false);
  const [receiptDate, setReceiptDate] = useState(getPacificToday());
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check-in saving
  const [savingCheckin, setSavingCheckin] = useState(false);

  // QR Scanner
  const [scannerEnabled, setScannerEnabled] = useState(true);
  const [scannerError, setScannerError] = useState<string | null>(null);
  const scannerRef = useRef<Html5QrcodeScanner | null>(null);
  const scannerInitialized = useRef(false);

  useEffect(() => {
    if (authLoading) {
      return; // Wait for auth to complete
    }

    if (!isAuthenticated) {
      navigate('/store/login');
      return;
    }

    setPageLoading(false);
    loadRecentCheckins();

    // Check for code param from QR scan
    const codeParam = searchParams.get('code');
    if (codeParam) {
      setSearchCode(codeParam);
      handleSearch(codeParam, '');
    }
  }, [authLoading, isAuthenticated, navigate, searchParams]);

  // Parse member code from QR data (URL or raw code)
  const parseQRCode = useCallback((qrData: string): string | null => {
    // Try to parse as URL first
    try {
      const url = new URL(qrData);
      const code = url.searchParams.get('code');
      if (code) return code.toUpperCase();
    } catch {
      // Not a URL, check if it's a raw member code
    }

    // Check if it matches member code pattern (RBF-XXXXXX)
    const codeMatch = qrData.match(/RBF-[A-Z0-9]{6}/i);
    if (codeMatch) {
      return codeMatch[0].toUpperCase();
    }

    return null;
  }, []);

  // Handle successful QR scan
  const onQRCodeScanned = useCallback((decodedText: string) => {
    console.log('[QR Scanner] Scanned:', decodedText);

    const memberCode = parseQRCode(decodedText);
    if (memberCode) {
      // Stop scanner and search for member
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
      }
      setScannerEnabled(false);
      setSearchCode(memberCode);
      handleSearch(memberCode, '');
    } else {
      setScannerError('Invalid QR code. Please try again or use manual lookup.');
      setTimeout(() => setScannerError(null), 3000);
    }
  }, [parseQRCode]);

  // Initialize QR scanner
  useEffect(() => {
    if (!scannerEnabled || view !== 'scan' || pageLoading || authLoading) {
      return;
    }

    // Prevent double initialization
    if (scannerInitialized.current) {
      return;
    }

    const initScanner = () => {
      // Check if element exists
      const element = document.getElementById('qr-reader');
      if (!element) {
        console.log('[QR Scanner] Element not found, retrying...');
        setTimeout(initScanner, 100);
        return;
      }

      scannerInitialized.current = true;

      const scanner = new Html5QrcodeScanner(
        'qr-reader',
        {
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1,
          supportedScanTypes: [Html5QrcodeScanType.SCAN_TYPE_CAMERA],
          rememberLastUsedCamera: true,
          showTorchButtonIfSupported: true,
          // Prefer rear camera for QR scanning
          videoConstraints: {
            facingMode: { ideal: 'environment' }
          }
        },
        /* verbose= */ false
      );

      scanner.render(
        (decodedText) => {
          onQRCodeScanned(decodedText);
        },
        (errorMessage) => {
          // Ignore scanning errors (happens continuously while scanning)
        }
      );

      scannerRef.current = scanner;
    };

    // Small delay to ensure DOM is ready
    const timeout = setTimeout(initScanner, 200);

    return () => {
      clearTimeout(timeout);
      if (scannerRef.current) {
        scannerRef.current.clear().catch(console.error);
        scannerRef.current = null;
        scannerInitialized.current = false;
      }
    };
  }, [scannerEnabled, view, pageLoading, authLoading, onQRCodeScanned]);

  // Re-enable scanner when returning to scan view
  const handleEnableScanner = () => {
    scannerInitialized.current = false;
    setScannerEnabled(true);
    setScannerError(null);
  };

  const loadRecentCheckins = async () => {
    try {
      const response = await api.get('/store/checkin/recent/');
      setRecentCheckins(response.data || []);
    } catch (error) {
      console.error('Failed to load recent checkins:', error);
    }
  };

  const handleSearch = async (code?: string, phone?: string) => {
    const searchCodeValue = code ?? searchCode;
    const searchPhoneValue = phone ?? searchPhone;

    if (!searchCodeValue && !searchPhoneValue) {
      setSearchError('Please enter a member code or phone number');
      return;
    }

    setSearching(true);
    setSearchError('');

    try {
      const params = new URLSearchParams();
      if (searchCodeValue) params.append('code', searchCodeValue);
      if (searchPhoneValue) params.append('phone', searchPhoneValue);

      const response = await api.get(`/store/checkin/?${params.toString()}`);
      setMemberData(response.data);
      setView('profile');

      // Load feed status
      loadFeedStatus(response.data.member.id);
    } catch (error: any) {
      setSearchError(error.response?.data?.error || 'Member not found');
    } finally {
      setSearching(false);
    }
  };

  const loadFeedStatus = async (memberId: number) => {
    setLoadingFeedStatus(true);
    try {
      const response = await api.get(`/store/member/${memberId}/feed-status/`);
      setFeedStatus(response.data.feed_status || []);
    } catch (error) {
      console.error('Failed to load feed status:', error);
    } finally {
      setLoadingFeedStatus(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !memberData) return;

    setSavingNote(true);
    try {
      const response = await api.post(`/store/member/${memberData.member.id}/notes/`, {
        note: newNote.trim()
      });

      // Add to local state
      setMemberData(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          staff_notes: [response.data.note, ...prev.staff_notes]
        };
      });
      setNewNote('');
    } catch (error) {
      console.error('Failed to add note:', error);
      alert('Failed to save note');
    } finally {
      setSavingNote(false);
    }
  };

  const handleScanReceipt = () => {
    // Show the live camera for receipt capture
    setShowCamera(true);
  };

  const handleCameraCapture = async (imageBase64: string) => {
    setShowCamera(false);
    if (!memberData) return;

    setScanningReceipt(true);
    setView('receipt');

    try {
      // Send to API
      const response = await api.post('/store/scan-receipt/', {
        image: imageBase64,
        member_id: memberData.member.id
      });

      if (response.data.success && response.data.receipt_data) {
        const data = response.data.receipt_data;
        setReceiptItems(data.items || []);
        setReceiptTotal(data.total || 0);
        if (data.date) setReceiptDate(data.date);
      } else {
        alert('Could not read receipt. Please enter items manually.');
        setReceiptItems([{ name: '', quantity: 1, price: 0 }]);
      }
    } catch (error) {
      console.error('Failed to scan receipt:', error);
      alert('Failed to process receipt image. Please enter items manually.');
      setReceiptItems([{ name: '', quantity: 1, price: 0 }]);
    } finally {
      setScanningReceipt(false);
    }
  };

  const handleCameraClose = () => {
    setShowCamera(false);
  };

  const handleReceiptImage = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !memberData) return;

    setScanningReceipt(true);
    setView('receipt');

    try {
      // Convert to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Send to API
      const response = await api.post('/store/scan-receipt/', {
        image: base64,
        member_id: memberData.member.id
      });

      if (response.data.success && response.data.receipt_data) {
        const data = response.data.receipt_data;
        setReceiptItems(data.items || []);
        setReceiptTotal(data.total || 0);
        if (data.date) setReceiptDate(data.date);
      } else {
        alert('Could not read receipt. Please enter items manually.');
        setReceiptItems([{ name: '', quantity: 1, price: 0 }]);
      }
    } catch (error) {
      console.error('Failed to scan receipt:', error);
      alert('Failed to process receipt image. Please enter items manually.');
      setReceiptItems([{ name: '', quantity: 1, price: 0 }]);
    } finally {
      setScanningReceipt(false);
    }
  };

  const handleReceiptItemChange = (index: number, field: keyof ReceiptItem, value: string | number) => {
    setReceiptItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleAddReceiptItem = () => {
    setReceiptItems(prev => [...prev, { name: '', quantity: 1, price: 0 }]);
  };

  const handleRemoveReceiptItem = (index: number) => {
    setReceiptItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveCheckin = async () => {
    if (!memberData) return;

    setSavingCheckin(true);
    try {
      await api.post('/store/checkin/save/', {
        member_id: memberData.member.id,
        items: receiptItems,
        total: receiptTotal,
        purchase_date: receiptDate
      });

      setView('saved');
      loadRecentCheckins();
    } catch (error) {
      console.error('Failed to save checkin:', error);
      alert('Failed to save check-in');
    } finally {
      setSavingCheckin(false);
    }
  };

  const handleNextCustomer = () => {
    setView('scan');
    setMemberData(null);
    setFeedStatus([]);
    setReceiptItems([]);
    setReceiptTotal(0);
    setSearchCode('');
    setSearchPhone('');
    setSearchError('');
    setNewNote('');
    setReceiptDate(getPacificToday());
    // Re-enable scanner for next customer
    handleEnableScanner();
  };

  const handleRecentCheckinClick = (checkin: RecentCheckin) => {
    setSearchCode('');
    handleSearch('', '');  // Need to look up by ID - for now just clear
  };

  const getAnimalEmoji = (type: string) => {
    const normalizedType = type.toLowerCase().replace(/\s+/g, '_');
    return animalEmojis[normalizedType] || '🐾';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  // Scan/Lookup View
  const renderScanView = () => (
    <div className="checkin-scan-view">
      <div className="scan-header">
        <h2>Scan Customer's Club Card</h2>
        <p>Use camera to scan QR code or enter details below</p>
      </div>

      <div className="scan-area">
        {scannerEnabled ? (
          <>
            <div id="qr-reader" className="qr-scanner-container"></div>
            {scannerError && (
              <div className="scanner-error">{scannerError}</div>
            )}
          </>
        ) : (
          <div className="scan-placeholder">
            <div className="scan-icon">✓</div>
            <p>Scanner paused</p>
            <button
              className="enable-scanner-btn"
              onClick={handleEnableScanner}
            >
              Enable Scanner
            </button>
          </div>
        )}
      </div>

      <div className="manual-lookup">
        <h3>Or look up manually:</h3>

        <div className="lookup-field">
          <label>Member Code</label>
          <div className="lookup-input-group">
            <input
              type="text"
              placeholder="RBF-XXXXXX"
              value={searchCode}
              onChange={(e) => setSearchCode(e.target.value.toUpperCase())}
              className="lookup-input"
            />
            <button
              onClick={() => handleSearch()}
              disabled={searching || !searchCode}
              className="lookup-btn"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        <div className="lookup-divider">or</div>

        <div className="lookup-field">
          <label>Phone Number</label>
          <div className="lookup-input-group">
            <input
              type="tel"
              placeholder="(509) 555-1234"
              value={searchPhone}
              onChange={(e) => setSearchPhone(e.target.value)}
              className="lookup-input"
            />
            <button
              onClick={() => handleSearch()}
              disabled={searching || !searchPhone}
              className="lookup-btn"
            >
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {searchError && (
          <div className="search-error">{searchError}</div>
        )}
      </div>

      {recentCheckins.length > 0 && (
        <div className="recent-checkins">
          <h3>Recent Check-ins Today</h3>
          <div className="recent-list">
            {recentCheckins.map(checkin => (
              <div key={checkin.id} className="recent-item">
                <div className="recent-info">
                  <span className="recent-name">{checkin.member_name}</span>
                  <span className="recent-time">{formatTime(checkin.checkin_time)}</span>
                </div>
                <span className="recent-items">
                  {checkin.items_count} item{checkin.items_count !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  // Profile View
  const renderProfileView = () => {
    if (!memberData) return null;

    const { member, last_visit, animals, recent_purchases, staff_notes } = memberData;

    return (
      <div className="checkin-profile-view">
        {/* Header Card */}
        <div className="profile-header-card">
          <div className="profile-header-main">
            <h2 className="profile-name">{member.name}</h2>
            <p className="profile-location">{member.city}, {member.state}</p>
          </div>
          <div className="profile-header-meta">
            <span className="profile-phone">{member.phone}</span>
            <span className="profile-since">Member since {formatDate(member.joined_date)}</span>
          </div>
          <div className="profile-visit-status">
            {last_visit.is_first_visit ? (
              <span className="visit-badge first-visit">First visit!</span>
            ) : (
              <span className="visit-badge">
                Last visit: {last_visit.days_ago === 0 ? 'Today' : `${last_visit.days_ago} day${last_visit.days_ago !== 1 ? 's' : ''} ago`}
              </span>
            )}
          </div>
        </div>

        {/* Animals Section */}
        {animals.length > 0 && (
          <div className="profile-section">
            <h3 className="section-title">Animals</h3>
            <div className="animals-grid">
              {animals.map(animal => (
                <div key={animal.id} className="animal-item">
                  <span className="animal-emoji">{getAnimalEmoji(animal.animal_type)}</span>
                  <div className="animal-info">
                    <span className="animal-count">{animal.count}</span>
                    <span className="animal-type">{animal.name || animal.animal_type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Feed Status Section */}
        {feedStatus.length > 0 && (
          <div className="profile-section">
            <h3 className="section-title">Feed Status</h3>
            <div className="feed-status-list">
              {feedStatus.map(status => (
                <div key={status.animal_id} className={`feed-status-item status-${status.status}`}>
                  <div className="feed-status-info">
                    <span className="feed-animal">
                      {getAnimalEmoji(status.animal_type)} {status.animal_name || status.animal_type}
                    </span>
                    <span className="feed-product">{status.product_name || 'No purchase data'}</span>
                  </div>
                  <div className="feed-status-level">
                    {status.days_remaining !== null ? (
                      <>
                        <div className="feed-progress">
                          <div
                            className="feed-progress-bar"
                            style={{ width: `${Math.min(100, (status.days_remaining / 30) * 100)}%` }}
                          />
                        </div>
                        <span className="feed-days">~{status.days_remaining} days</span>
                      </>
                    ) : (
                      <span className="feed-unknown">Unknown</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Staff Notes Section */}
        <div className="profile-section">
          <div className="section-title-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="section-title">Staff Notes</h3>
            <VoiceRecorder
              endpoint="/api/voice/transcribe/store/"
              variant="icon"
              summarize
              additionalData={memberData ? { member_id: String(memberData.member.id) } : undefined}
              onTranscriptionComplete={(result) => {
                // Set the transcribed text as the new note
                setNewNote(prev => {
                  if (prev.trim()) {
                    return `${prev}\n\n${result.text}`;
                  }
                  return result.text;
                });
              }}
            />
          </div>
          <div className="notes-input-group">
            <textarea
              placeholder="Add a note about this customer... (or tap mic to record)"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="notes-input"
              rows={2}
            />
            <button
              onClick={handleAddNote}
              disabled={!newNote.trim() || savingNote}
              className="notes-save-btn"
            >
              {savingNote ? 'Saving...' : 'Add Note'}
            </button>
          </div>
          {staff_notes.length > 0 && (
            <div className="notes-list">
              {staff_notes.map(note => (
                <div key={note.id} className="note-item">
                  <p className="note-text">{note.note}</p>
                  <div className="note-meta">
                    <span className="note-author">{note.created_by}</span>
                    <span className="note-date">{formatDate(note.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Purchases Section */}
        {recent_purchases.length > 0 && (
          <div className="profile-section">
            <h3 className="section-title">Recent Purchases</h3>
            <div className="purchases-list">
              {recent_purchases.map(purchase => (
                <div key={purchase.id} className="purchase-item">
                  <div className="purchase-info">
                    <span className="purchase-product">{purchase.product_name}</span>
                    <span className="purchase-date">{formatDate(purchase.date)}</span>
                  </div>
                  <span className="purchase-qty">
                    {purchase.quantity}x
                    {purchase.total_lbs && ` (${purchase.total_lbs} lbs)`}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="profile-actions">
          <button onClick={handleScanReceipt} className="action-btn primary">
            <span className="btn-icon">📸</span>
            Scan Receipt
          </button>
          <button onClick={() => setView('receipt')} className="action-btn secondary">
            <span className="btn-icon">✏️</span>
            Manual Entry
          </button>
          <button onClick={handleNextCustomer} className="action-btn outline">
            Cancel
          </button>
        </div>

        <input
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleReceiptImage}
          ref={fileInputRef}
          style={{ display: 'none' }}
        />
      </div>
    );
  };

  // Receipt Entry View
  const renderReceiptView = () => (
    <div className="checkin-receipt-view">
      <div className="receipt-header">
        <button onClick={() => setView('profile')} className="back-btn">
          ← Back
        </button>
        <h2>Receipt Entry</h2>
        <div className="receipt-member">
          {memberData?.member.name}
        </div>
      </div>

      {scanningReceipt ? (
        <div className="receipt-scanning">
          <div className="scanning-spinner"></div>
          <p>Processing receipt...</p>
        </div>
      ) : (
        <>
          <div className="receipt-date-field">
            <label>Purchase Date</label>
            <input
              type="date"
              value={receiptDate}
              onChange={(e) => setReceiptDate(e.target.value)}
              className="date-input"
            />
          </div>

          <div className="receipt-items">
            <h3>Items</h3>
            {receiptItems.map((item, index) => (
              <div key={index} className="receipt-item">
                <input
                  type="text"
                  placeholder="Item name"
                  value={item.name}
                  onChange={(e) => handleReceiptItemChange(index, 'name', e.target.value)}
                  className="item-name-input"
                />
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="Qty"
                  value={item.quantity}
                  onChange={(e) => handleReceiptItemChange(index, 'quantity', e.target.value)}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    handleReceiptItemChange(index, 'quantity', Math.max(1, val));
                  }}
                  className="item-qty-input"
                />
                <input
                  type="text"
                  inputMode="decimal"
                  placeholder="Price"
                  value={item.price}
                  onChange={(e) => handleReceiptItemChange(index, 'price', e.target.value)}
                  onBlur={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    handleReceiptItemChange(index, 'price', Math.max(0, val));
                  }}
                  className="item-price-input"
                />
                <button
                  onClick={() => handleRemoveReceiptItem(index)}
                  className="item-remove-btn"
                >
                  ×
                </button>
              </div>
            ))}
            <button onClick={handleAddReceiptItem} className="add-item-btn">
              + Add Item
            </button>
          </div>

          <div className="receipt-total">
            <label>Total</label>
            <div className="total-input-group">
              <span className="currency-symbol">$</span>
              <input
                type="text"
                inputMode="decimal"
                value={receiptTotal}
                onChange={(e) => setReceiptTotal(e.target.value as any)}
                onBlur={(e) => {
                  const val = parseFloat(e.target.value) || 0;
                  setReceiptTotal(Math.max(0, val));
                }}
                className="total-input"
              />
            </div>
          </div>

          <div className="receipt-actions">
            <button
              onClick={handleSaveCheckin}
              disabled={savingCheckin || receiptItems.length === 0}
              className="save-btn"
            >
              {savingCheckin ? 'Saving...' : 'Save Purchase'}
            </button>
          </div>
        </>
      )}
    </div>
  );

  // Saved View
  const renderSavedView = () => (
    <div className="checkin-saved-view">
      <div className="saved-icon">✓</div>
      <h2>Purchase Saved!</h2>
      <p className="saved-message">
        Check-in recorded for <strong>{memberData?.member.name}</strong>
      </p>
      <button onClick={handleNextCustomer} className="next-customer-btn">
        Next Customer
      </button>
    </div>
  );

  // Show loading state while auth is being checked
  if (authLoading || pageLoading) {
    return (
      <>
        <StoreHeader />
        <div className="store-checkin-page">
          <div className="checkin-loading">
            <div className="loading-spinner"></div>
            <p>Loading...</p>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <StoreHeader />
      <div className="store-checkin-page">
        {view === 'scan' && renderScanView()}
        {view === 'profile' && renderProfileView()}
        {view === 'receipt' && renderReceiptView()}
        {view === 'saved' && renderSavedView()}
      </div>

      {/* Camera Capture Modal for Receipt Scanning */}
      {showCamera && (
        <CameraCapture
          onCapture={handleCameraCapture}
          onClose={handleCameraClose}
          facingMode="environment"
          title="Scan Receipt"
        />
      )}
    </>
  );
};

export default StoreCheckin;
